/**
 * <article-toc> — table of contents enhancement (P11).
 *
 * The section server-renders the box, the title and any entries derivable from
 * the merchant HTML body (h2s). Liquid cannot read sibling sections' headings,
 * so — like live's implementation — entries for section-rendered headings
 * ([data-toc-target]) are collected client-side.
 *
 * CLS containment (this is where live's TOC hurt the baseline):
 * - Mobile: the list lives inside a closed <details>; filling it changes no
 *   geometry until the reader opens it (user-initiated, outside CLS).
 * - Desktop (>= 990px): the section is a right-floated rail; this element opens
 *   the <details> and fills the list, which extends the float downward without
 *   displacing the article column (block boxes do not reflow around floats).
 *
 * Also provides the scroll progress indicator (reserved meter box) and assigns
 * heading ids: positional `article-body-h2-N` for body headings (matching the
 * server-rendered links) and generated ids for section headings.
 */
class ArticleToc extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-toc-list]');
    this.indicator = this.querySelector('[data-toc-indicator]');
    this.disclosure = this.querySelector('details');
    if (!this.list) return;

    this.hideH3s = this.dataset.hideH3s === 'true';
    this.headings = this.collectHeadings();
    this.renderList();

    if (window.matchMedia('(min-width: 990px)').matches && this.disclosure) {
      this.disclosure.open = true;
    }

    if (this.headings.length && this.indicator) {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  collectHeadings() {
    const selector = '[data-article-body] h2, [data-article-body] h3, [data-toc-target]';
    const seen = [];
    let bodyH2 = 0;
    let generated = 0;
    document.querySelectorAll(selector).forEach((heading) => {
      if (heading.hasAttribute('data-toc-skip')) return;
      const level = heading.tagName.toLowerCase();
      if (level === 'h3' && this.hideH3s) return;
      if (level !== 'h2' && level !== 'h3') return;
      const text = heading.textContent.trim();
      if (!text) return;
      if (!heading.id) {
        if (level === 'h2' && heading.closest('[data-article-body]')) {
          bodyH2 += 1;
          heading.id = `article-body-h2-${bodyH2}`;
        } else {
          generated += 1;
          heading.id = `article-toc-heading-${generated}`;
        }
      }
      seen.push({ heading, level, text });
    });
    return seen;
  }

  renderList() {
    if (!this.headings.length) return;
    // Rebuild (rather than append to) the server-rendered body-only entries so
    // section headings land in document order.
    this.list.textContent = '';
    this.headings.forEach(({ heading, level, text }) => {
      const item = document.createElement('li');
      item.className = level === 'h3' ? 'article-toc__item article-toc__item--sub' : 'article-toc__item';
      const link = document.createElement('a');
      link.className = 'article-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = text;
      const description = heading.getAttribute('data-toc-description');
      if (description) link.title = description;
      item.appendChild(link);
      this.list.appendChild(item);
    });
    this.items = Array.from(this.list.children);
  }

  onScroll() {
    if (this.scrollScheduled) return;
    this.scrollScheduled = true;
    window.requestAnimationFrame(() => {
      this.scrollScheduled = false;
      this.updateIndicator();
    });
  }

  updateIndicator() {
    const fromTop = window.scrollY + window.innerHeight * 0.3;
    let activeIndex = 0;
    this.headings.forEach(({ heading }, index) => {
      const top = heading.getBoundingClientRect().top + window.scrollY;
      if (top <= fromTop) activeIndex = index;
    });

    const meter = this.indicator.parentElement;
    const travel = Math.max(0, meter.clientHeight - this.indicator.clientHeight);
    const progress = this.headings.length > 1 ? activeIndex / (this.headings.length - 1) : 0;
    this.indicator.style.top = `${Math.round(travel * progress)}px`;

    if (this.items) {
      this.items.forEach((item, index) => {
        item.classList.toggle('article-toc__item--active', index === activeIndex);
      });
    }
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
